import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { GlowCard } from '../components/ui/GlowCard'
import { api } from '../lib/api'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { UpgradeBanner } from '../components/ui/UpgradeBanner'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { Badge } from '../components/ui/Badge'
import { Drawer } from '../components/ui/Drawer'
import { useToast } from '../components/ui/Toast'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
const PlayIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
const EditIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
const TrashIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
const ZapIcon = <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>

interface WorkflowRow {
  id: string
  name: string
  description: string | null
  trigger_type: string
  trigger_event_key: string | null
  status: string
  enabled: number
  run_count: number
  error_count: number
  last_run: string | null
  last_run_status: string | null
  created_at: string
  updated_at: string
}

interface TemplateRow {
  id: string
  name: string
  description: string | null
  category: string
  icon: string | null
  difficulty: string
  tags: string | null
  use_count: number
}

const STATUS_MAP: Record<string, { label: string; v: 'ok' | 'warn' | 'err' | 'default' | 'accent' | 'info' }> = {
  active: { label: 'Active', v: 'ok' },
  draft: { label: 'Draft', v: 'default' },
  paused: { label: 'Paused', v: 'warn' },
  error: { label: 'Error', v: 'err' },
  archived: { label: 'Archived', v: 'default' },
}

const TRIGGER_MAP: Record<string, string> = {
  event: 'Event',
  cron: 'Schedule',
  webhook: 'Webhook',
  manual: 'Manual',
}

export default function Workflows() {
  const { enabled, loading: flagLoading, requiredPlan } = useFeatureFlag('workflow_automation')
  const { toast, toastError } = useToast()
  const navigate = useNavigate()

  const [workflows, setWorkflows] = useState<WorkflowRow[]>([])
  const [templates, setTemplates] = useState<TemplateRow[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [templateDrawer, setTemplateDrawer] = useState(false)
  const [creating, setCreating] = useState(false)

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      const [wfRes, tmplRes] = await Promise.all([
        api.get<{ data: WorkflowRow[] }>(`/workflows?${params}`).catch(() => ({ data: [] })),
        api.get<{ data: TemplateRow[] }>('/workflows/templates/all').catch(() => ({ data: [] })),
      ])
      setWorkflows(wfRes.data || [])
      setTemplates(tmplRes.data || [])
    } catch (err) {
      toastError('Failed to load workflows', err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, toastError])

  useEffect(() => {
    if (enabled) loadData()
  }, [enabled, loadData])

  const handleCreateBlank = async () => {
    setCreating(true)
    try {
      const blankDef = JSON.stringify({
        nodes: [{ id: 'trigger', type: 'trigger_manual', position: { x: 250, y: 50 }, data: { label: 'Run Workflow' } }],
        edges: [],
        trigger: { type: 'manual' },
        settings: { timezone: 'UTC', error_handling: 'stop' },
      })
      const wf = await api.post<WorkflowRow>('/workflows', {
        name: 'Untitled Workflow',
        definition: blankDef,
        trigger_type: 'manual',
      })
      toast('Workflow created')
      navigate(`/workflows/${wf.id}/edit`)
    } catch (err) {
      toastError('Failed to create workflow', err)
    } finally {
      setCreating(false)
    }
  }

  const handleCreateFromTemplate = async (template: TemplateRow) => {
    setCreating(true)
    try {
      const wf = await api.post<WorkflowRow>(`/workflows/templates/${template.id}/create`, {
        name: template.name,
      })
      toast('Workflow created from template')
      navigate(`/workflows/${wf.id}/edit`)
    } catch (err) {
      toastError('Failed to create workflow', err)
    } finally {
      setCreating(false)
      setTemplateDrawer(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/workflows/${id}`)
      toast('Workflow deleted')
      setWorkflows(prev => prev.filter(w => w.id !== id))
    } catch (err) {
      toastError('Failed to delete workflow', err)
    }
  }

  const handleToggle = async (wf: WorkflowRow) => {
    try {
      const newEnabled = wf.enabled ? 0 : 1
      const newStatus = newEnabled ? 'active' : 'paused'
      await api.patch(`/workflows/${wf.id}`, { enabled: newEnabled, status: newStatus })
      setWorkflows(prev => prev.map(w => w.id === wf.id ? { ...w, enabled: newEnabled, status: newStatus } : w))
      toast(newEnabled ? 'Workflow enabled' : 'Workflow paused')
    } catch (err) {
      toastError('Failed to update workflow', err)
    }
  }

  const handleExecute = async (id: string) => {
    try {
      await api.post(`/workflows/${id}/execute`, {})
      toast('Workflow execution started')
    } catch (err) {
      toastError('Failed to execute workflow', err)
    }
  }

  if (flagLoading) return <div style={{ padding: 32 }}><TableSkeleton rows={5} /></div>
  if (!enabled) return <UpgradeBanner feature="Workflow Automation" plan={requiredPlan} />

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Workflows</h1>
          <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '4px 0 0' }}>
            Automate tasks with visual workflow builder
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => setTemplateDrawer(true)} icon={ZapIcon}>
            Templates
          </Button>
          <Button onClick={handleCreateBlank} loading={creating} icon={PlusIcon}>
            New Workflow
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['', 'active', 'draft', 'paused', 'error'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: '4px 12px',
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 'var(--r-f)',
              border: '1px solid',
              borderColor: statusFilter === s ? 'var(--amber)' : 'var(--border)',
              background: statusFilter === s ? 'var(--accent-m)' : 'transparent',
              color: statusFilter === s ? 'var(--amber)' : 'var(--text-2)',
              cursor: 'pointer',
              fontFamily: 'var(--font)',
            }}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Workflow List */}
      {loading ? (
        <TableSkeleton rows={5} />
      ) : workflows.length === 0 ? (
        <EmptyState
          icon={ZapIcon}
          title="No workflows yet"
          description="Create your first workflow to automate tasks like notifications, record creation, and integrations."
          action={{ label: 'Create Workflow', onClick: handleCreateBlank }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {workflows.map(wf => {
            const st = STATUS_MAP[wf.status] || STATUS_MAP.draft
            return (
              <GlowCard key={wf.id} onClick={() => navigate(`/workflows/${wf.id}/edit`)} style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{wf.name}</span>
                      <Badge v={st.v}>{st.label}</Badge>
                      <Badge v="info">{TRIGGER_MAP[wf.trigger_type] || wf.trigger_type}</Badge>
                    </div>
                    {wf.description && (
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>{wf.description}</div>
                    )}
                    <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                      <span>{wf.run_count} runs</span>
                      {wf.error_count > 0 && <span style={{ color: 'var(--err)' }}>{wf.error_count} errors</span>}
                      {wf.last_run && <span>Last: {new Date(wf.last_run).toLocaleDateString()}</span>}
                      {wf.last_run_status && (
                        <Badge v={wf.last_run_status === 'completed' ? 'ok' : wf.last_run_status === 'failed' ? 'err' : 'default'} sm>
                          {wf.last_run_status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" iconOnly icon={PlayIcon} title="Execute" onClick={() => handleExecute(wf.id)} />
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      icon={<span style={{ fontSize: 16, lineHeight: 1 }}>{wf.enabled ? '⏸' : '▶'}</span>}
                      title={wf.enabled ? 'Pause' : 'Enable'}
                      onClick={() => handleToggle(wf)}
                    />
                    <Button size="sm" variant="ghost" iconOnly icon={EditIcon} title="Edit" onClick={() => navigate(`/workflows/${wf.id}/edit`)} />
                    <Button size="sm" variant="ghost" iconOnly icon={TrashIcon} title="Delete" onClick={() => handleDelete(wf.id)} />
                  </div>
                </div>
              </GlowCard>
            )
          })}
        </div>
      )}

      {/* Template Drawer */}
      <Drawer isOpen={templateDrawer} onClose={() => setTemplateDrawer(false)} title="Workflow Templates" maxWidth={640}>
        <div style={{ padding: 16 }}>
          {templates.length === 0 ? (
            <EmptyState icon={ZapIcon} title="No templates available" description="Templates will appear here as they are added." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {templates.map(t => {
                let tags: string[] = []
                try { tags = JSON.parse(t.tags || '[]') } catch { /* empty */ }
                return (
                  <GlowCard key={t.id} onClick={() => handleCreateFromTemplate(t)} style={{ padding: '14px 18px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 'var(--r-m)',
                        background: 'var(--accent-m)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--amber)', fontSize: 16, flexShrink: 0,
                      }}>
                        {ZapIcon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>{t.name}</div>
                        {t.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{t.description}</div>}
                        <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                          <Badge v="accent" sm>{t.category}</Badge>
                          <Badge sm>{t.difficulty}</Badge>
                          {tags.map(tag => <Badge key={tag} sm>{tag}</Badge>)}
                        </div>
                      </div>
                    </div>
                  </GlowCard>
                )
              })}
            </div>
          )}
        </div>
      </Drawer>
    </div>
  )
}
